// #92 AttendanceRoll (debrief D-3): the Attendance roll card. Two groups of person rows — the required attendees in
// the room (each with the *Required* tag) and the *Missing attendee* rows (the red pill and the reason line) — with a
// status column whose tick is the Roll Call control (one tab stop per row; ticking moves a person between the groups).
// Under a divider sit the Coordinator and the TA Manager with their *No vote* / *Oversight* tags (BRD §13): they are
// not on the roll. After Roll Call the roll is read-only (`mode="view"`): the ticks become glyphs. The positive word for
// an attendee in the room is open (Q-E18), so a ticked row says it with the checked state, not with a word.

import { useRef, useState, type ReactNode } from "react";
import { cx } from "../lib/cx";
import { useIsoLayoutEffect } from "../lib/useIsoLayoutEffect";
import { useSvgId } from "../lib/useSvgId";
import { ROLE_LABELS, COMMON_LABELS, DEBRIEF_STATUS } from "../labels";
import type { Blocked } from "../core/Button";
import { Card } from "../core/Card";
import { Icon } from "../core/icons";
import { SURFACE_LABELS } from "../core/labels";
import { Person } from "../core/Person";
import { Pill } from "../core/Pill";
import { Tag } from "../core/Tag";
import { Divider, SectionTitle } from "../core/Text";
import { DEBRIEF_ROOM_LABELS } from "./labels";
import { CheckTick } from "./Tick";
import type { Hs } from "../lib/hs";

export interface RollAttendee {
  key: string;
  name: string;
  /** Their seat in the room (Recruiter, HM, HRBP, Interviewer). */
  seat: string;
  hueKey?: string | undefined;
  /** In the room. */
  present: boolean;
  /** Why a missing attendee is missing (shown under the row in --text-3). */
  reason?: ReactNode | undefined;
  hs?: Hs | undefined;
}

export interface RollObserver {
  key: string;
  name: string;
  hueKey?: string | undefined;
  /** coordinator: *Coordinator* · *No vote*; taManager: *TA Manager* · *Oversight*. */
  seat: "coordinator" | "taManager";
}

export interface AttendanceRollProps {
  attendees: readonly RollAttendee[];
  observers?: readonly RollObserver[] | undefined;
  /** Roll Call: a tick flips a person in or out of the room. Uncontrolled when absent. */
  onToggle?: ((key: string, present: boolean) => void) | undefined;
  /** The card's status pill (Roll Call while the roll is taken). */
  status?: string | undefined;
  can?: boolean | undefined;
  blocked?: Blocked | undefined;
  /** "view": read-only after Roll Call. */
  mode?: "edit" | "view" | undefined;
  className?: string | undefined;
}

const OBSERVER_TAG = { coordinator: DEBRIEF_ROOM_LABELS.noVote, taManager: DEBRIEF_ROOM_LABELS.oversight } as const;

function RollRow({ person, edit, blocked, onToggle, idBase, register }: {
  person: RollAttendee; edit: boolean; blocked: Blocked | undefined; onToggle: (present: boolean) => void; idBase: string;
  register: (element: HTMLButtonElement | null) => void;
}) {
  const nameId = `${idBase}-${person.key}`;
  const glyph = person.present
    ? <span className="mc-roll-g mc-roll-in" aria-hidden="true"><Icon name="check" size={14} /></span>
    : <span className="mc-roll-g mc-roll-out" aria-hidden="true"><Icon name="x" size={14} /></span>;
  return (
    <li className={cx("mc-roll-r", !person.present && "mc-roll-miss")}>
      <Person name={person.name} hueKey={person.hueKey} size={24} sub={person.seat} />
      <span className="mc-roll-t">
        {person.present ? <Tag>{COMMON_LABELS.required}</Tag> : <Pill tone="red">{DEBRIEF_ROOM_LABELS.missingAttendee}</Pill>}
      </span>
      <span className="mc-roll-s">
        <span className="mc-sr" id={nameId}>{person.name}</span>
        {edit ? <CheckTick ref={register} checked={person.present} onChange={onToggle} labelledBy={nameId} blocked={blocked} hs={person.hs} /> : glyph}
      </span>
      {!person.present && person.reason ? <span className="mc-roll-why">{person.reason}</span> : null}
    </li>
  );
}

export function AttendanceRoll({ attendees, observers = [], onToggle, status = DEBRIEF_STATUS.rollCall, can, blocked, mode, className }: AttendanceRollProps) {
  const id = useSvgId();
  const [own, setOwn] = useState<Readonly<Record<string, boolean>>>({});
  const ticks = useRef(new Map<string, HTMLButtonElement>());
  // A tick moves its person to the other group, so React mounts a new tick there: the person ticked keeps the focus.
  const follow = useRef<string | null>(null);
  useIsoLayoutEffect(() => {
    const key = follow.current;
    if (key === null) return;
    follow.current = null;
    const tick = ticks.current.get(key);
    if (tick && document.activeElement !== tick) tick.focus();
  });
  if (can === false) return null;
  const edit = mode !== "view";
  const people = attendees.map((person) => (person.key in own ? { ...person, present: own[person.key] === true } : person));
  const inRoom = people.filter((person) => person.present);
  const missing = people.filter((person) => !person.present);
  const toggle = (key: string) => (present: boolean) => {
    follow.current = key;
    if (onToggle) onToggle(key, present);
    else setOwn((held) => ({ ...held, [key]: present }));
  };
  const group = (title: string, rows: typeof people, gid: string) => (
    <div className="mc-roll-grp">
      <SectionTitle as="h4" id={gid} count={rows.length}>{title}</SectionTitle>
      <ul className="mc-roll" aria-labelledby={gid}>
        {rows.map((person) => (
          <RollRow key={person.key} person={person} edit={edit} blocked={blocked} onToggle={toggle(person.key)} idBase={`${id}-n`}
            register={(element) => {
              if (element) ticks.current.set(person.key, element);
              else if (ticks.current.get(person.key)?.isConnected === false) ticks.current.delete(person.key);
            }}
          />
        ))}
      </ul>
    </div>
  );
  return (
    <Card title={SURFACE_LABELS.attendanceRoll} pills={<Pill>{status}</Pill>} className={cx("mc-roll-card", className)}>
      {inRoom.length > 0 ? group(COMMON_LABELS.required, inRoom, `${id}-in`) : null}
      {missing.length > 0 ? group(DEBRIEF_ROOM_LABELS.missingAttendee, missing, `${id}-out`) : null}
      {observers.length > 0 ? (
        <>
          <Divider />
          <ul className="mc-roll mc-roll-obs">
            {observers.map((person) => (
              <li key={person.key} className="mc-roll-r">
                <Person name={person.name} hueKey={person.hueKey} size={24} sub={ROLE_LABELS[person.seat]} />
                <span className="mc-roll-s"><Tag>{OBSERVER_TAG[person.seat]}</Tag></span>
              </li>
            ))}
          </ul>
        </>
      ) : null}
    </Card>
  );
}
